import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Skeleton } from "@/components/ui";

export interface DataTableColumn<Row> {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  align?: "left" | "right";
  width: string;
  sortable?: boolean;
}

export interface DataTableSort {
  id: string;
  direction: "asc" | "desc";
}

interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  emptyMessage?: ReactNode;
}

export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  emptyMessage,
}: DataTableProps<Row>) {
  const template = columns.map((column) => column.width).join(" ");

  return (
    <div
      role="table"
      className="min-w-[720px] overflow-hidden rounded-md border border-border bg-surface-2"
    >
      <div
        role="row"
        className="sticky top-0 z-10 grid h-10 items-center border-b border-border
          bg-surface-1 px-4 text-small font-medium text-text-secondary"
        style={{ gridTemplateColumns: template }}
      >
        {columns.map((column) => {
          const active = sort?.id === column.id;
          const ariaSort = active
            ? sort.direction === "asc"
              ? "ascending"
              : "descending"
            : "none";
          return (
            <div
              key={column.id}
              role="columnheader"
              aria-sort={column.sortable ? ariaSort : undefined}
              className={cn(column.align === "right" && "text-right")}
            >
              {column.sortable && onSortChange ? (
                <button
                  type="button"
                  className="w-full text-inherit hover:text-text-primary"
                  onClick={() =>
                    onSortChange({
                      id: column.id,
                      direction: active && sort.direction === "desc" ? "asc" : "desc",
                    })
                  }
                >
                  {column.header}
                </button>
              ) : (
                column.header
              )}
            </div>
          );
        })}
      </div>
      <div role="rowgroup">
        {rows.length === 0 ? (
          <div
            role="row"
            className="grid min-h-32 items-center"
            style={{ gridTemplateColumns: template }}
          >
            <div
              role="cell"
              className="col-span-full py-12 text-center text-body text-text-secondary"
              style={{ gridColumn: "1 / -1" }}
            >
              {emptyMessage}
            </div>
          </div>
        ) : (
          rows.map((row) => (
            <div
              key={getRowId(row)}
              role="row"
              className="grid h-10 items-center border-b border-border-subtle px-4
                text-small text-text-primary last:border-b-0"
              style={{ gridTemplateColumns: template }}
            >
              {columns.map((column) => (
                <div
                  key={column.id}
                  role="cell"
                  className={cn("min-w-0", column.align === "right" && "text-right tnum")}
                >
                  {column.cell(row)}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function TableSkeleton({ rows = 12, columns = 4 }: { rows?: number; columns?: number }) {
  const widths = Array.from(
    { length: columns },
    (_, index) => (index === 0 ? "2fr" : "1fr"),
  ).join(" ");
  return (
    <div
      role="table"
      aria-label="Loading table"
      className="overflow-hidden rounded-md border border-border bg-surface-2"
    >
      <div
        role="row"
        className="grid h-10 border-b border-border bg-surface-1"
        style={{ gridTemplateColumns: widths }}
      />
      {Array.from({ length: rows }, (_, index) => (
        <div
          role="row"
          key={index}
          className="grid h-10 items-center gap-4 border-b border-border-subtle px-4 last:border-b-0"
          style={{ gridTemplateColumns: widths }}
        >
          {Array.from({ length: columns }, (_, cellIndex) => (
            <Skeleton key={cellIndex} className="h-4 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}
